import React from 'react';
import { Link } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SEOHead from '../components/SEOHead';
import BentoIllustration from '../components/BentoIllustration';

const About: React.FC = () => {
  useDocumentTitle('Cognidaily | About');

  const faqItems: Array<{ q: string; a: React.ReactNode }> = [
    { q: 'How does scoring work?', a: 'Accuracy plus speed bonus.' },
    { q: 'What are the quiz modes?', a: '5 topics, 10 questions each. One attempt per topic per day.' },
    { q: 'How do I earn badges?', a: 'Streaks, perfect scores, leaderboard finishes, milestones.' },
    { q: 'How are leaderboards calculated?', a: 'Daily, weekly, monthly. Ranked by score. Global and regional.' },
    { q: 'When do quizzes reset?', a: 'Midnight GMT+10.' },
    { q: 'Can I play multiple times per day?', a: 'One attempt per topic. 5 topics means 5 quizzes max per day.' },
    { q: 'Do I need to be logged in to play?', a: 'No. You can play as a guest. Your progress is saved on this device. Sign in to sync across devices and appear on leaderboards.' },
    { q: 'Quiz history?', a: <>Premium only. <Link to="/premium">Premium</Link></> },
    { q: 'Report a bug?', a: <><Link to="/contact">Contact us</Link></> }
  ];

  return (
    <>
      <SEOHead
        title="About"
        description="Daily trivia. 5 topics, 10 questions each. Accuracy and speed determine your score."
        keywords="trivia, daily quiz, leaderboard"
        url="https://cognidaily.io/about"
      />
      <article className="about-page">
        <section className="about-page__header">
          <h1>About</h1>
          <div className="about-page__intro">
            <p>
              Cognidaily is a competitive daily trivia platform.
            </p>
            <p>
            Each day you get five fixed topics: Pop Culture, Science &amp; Nature, History &amp; Politics, Geography &amp; World, and Sports &amp; Games, with 10 questions each. The quizzes refresh daily at midnight GMT+10. You get one attempt per topic per day, so up to five quizzes total.
            </p>
            <p>
            Your score combines accuracy and speed, and you can climb daily, weekly, and monthly leaderboards. Earn XP, levels, and badges as you play.
            </p>
          </div>
        </section>

        <section className="bento-section">
          <div className="bento-block bento-block--flip">
            <div className="bento-block__image">
              <BentoIllustration variant="how-it-works" idSuffix="about-2" />
            </div>
            <div className="bento-block__content">
              <h2>How it works</h2>
              <ol>
                <li>Pick a topic and play. 10 multiple-choice questions.</li>
                <li>Your score is accuracy plus a speed bonus.</li>
                <li>Rank on daily, weekly, and monthly leaderboards.</li>
                <li>Earn XP, levels, and badges.</li>
              </ol>
            </div>
          </div>
        </section>

        <section className="bento-section">
          <div className="bento-block bento-block--faq">
            <div className="bento-block__image bento-block__image--stretch">
              <BentoIllustration variant="faq" idSuffix="about-3" />
            </div>
            <div className="bento-block__content">
              <h2>FAQ</h2>
              <div className="faq-list">
                {faqItems.map((item, i) => (
                  <div key={i} className="faq-item faq-item--expanded">
                    <p className="faq-item__q">{item.q}</p>
                    <p className="faq-item__a">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="about-page__play">
          <p>Ready to compete?</p>
          <Link to="/compete" className="btn btn-primary">Play</Link>
        </section>
      </article>
    </>
  );
};

export default About;
